import { useState, type CSSProperties, type ReactNode, type RefObject } from 'react'
import { FloatingMenu, type FloatingMenuPlacement } from './FloatingMenu'
import { Pressable } from './Pressable'
import { getNlsString } from '../../nls/nlsRuntime'

export type DropdownItem = {
  key: string
  label: ReactNode
  onSelect?: () => void
  disabled?: boolean
  isActive?: boolean
  tone?: 'default' | 'warn'
  icon?: ReactNode
  meta?: ReactNode
  tourId?: string
}

interface Props {
  items: DropdownItem[]
  isOpen: boolean
  onClose: () => void
  anchorRef?: RefObject<HTMLElement | null>
  ariaLabel?: string
  className?: string
  style?: CSSProperties
  emptyLabel?: string
  minWidth?: number
  matchAnchorWidth?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  placement?: FloatingMenuPlacement
}

/**
 * Locked dropdown surface — skin D (glass chip rows).
 * Positioning + outside-click + portal are delegated to FloatingMenu so
 * the menu escapes ancestor `overflow: hidden` and stacking contexts and
 * auto-flips when it would overflow the viewport. Callers only supply the
 * anchor, the items, and open state.
 */
export const Dropdown = ({
  items,
  isOpen,
  onClose,
  anchorRef,
  ariaLabel,
  className,
  style,
  emptyLabel = getNlsString('NLSCommonEmptyNoOptions'),
  minWidth,
  matchAnchorWidth = false,
  searchable = false,
  searchPlaceholder = getNlsString('NLSCommonInputSearchPlaceholder'),
  placement = 'bottom-start',
}: Props) => {
  const [query, setQuery] = useState('')
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen)
    if (!isOpen) {
      setQuery('')
    }
  }

  if (!anchorRef) return null

  const visible = searchable && query
    ? items.filter((item) => {
        const text = typeof item.label === 'string' ? item.label : ''
        return text.toLowerCase().includes(query.trim().toLowerCase())
      })
    : items

  return (
    <FloatingMenu
      anchorRef={anchorRef}
      isOpen={isOpen}
      onClose={onClose}
      placement={placement}
      matchAnchorWidth={matchAnchorWidth}
      minWidth={minWidth}
      className={`dsDrop is-open ${className ?? ''}`.trim()}
      role="menu"
      ariaLabel={ariaLabel}
      style={style}
    >
      {searchable && (
        <div className="dsDrop__search">
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
          />
        </div>
      )}
      <div className="dsDrop__items">
        {visible.length === 0 ? (
          <span className="dsDrop__empty">{emptyLabel}</span>
        ) : (
          visible.map((item) => (
            <Pressable
              key={item.key}
              role="menuitem"
              disabled={item.disabled}
              data-tone={item.tone === 'warn' ? 'warn' : undefined}
              data-tour-id={item.tourId}
              className={`dsDrop__item${item.isActive ? ' is-active' : ''}`}
              onClick={() => {
                if (item.disabled) return
                item.onSelect?.()
                onClose()
              }}
            >
              {item.icon && <span className="dsDrop__itemIcon" aria-hidden="true">{item.icon}</span>}
              <span className="dsDrop__itemLabel">{item.label}</span>
              {item.meta && <span className="dsDrop__itemMeta">{item.meta}</span>}
            </Pressable>
          ))
        )}
      </div>
    </FloatingMenu>
  )
}
